/*global df, global */

import { Feature, Map, View } from 'ol';
import { unByKey } from 'ol/Observable';
import { isEmpty } from 'ol/extent';
import { Point } from 'ol/geom';
import { Draw, Modify, Select, Snap, Translate } from 'ol/interaction';
import { createBox, createRegularPolygon } from 'ol/interaction/Draw';
import { Vector as VectorLayer, WebGLTile as WebGLTileLayer } from 'ol/layer';
import { fromLonLat, toLonLat } from 'ol/proj';
import { Google, Vector as VectorSource } from 'ol/source';
import './autocomplete';
import './index.css';
import { exportMap, geoJSON, styleFunction, toStyleFunction } from './ol-utils';

let decimalSeparator;

// Use this to convert numbers received from the server (as strings) to numbers
function stringToNum(string) {
    return df.sys.data.stringToNum(string, decimalSeparator);
}

// Use this to convert numbers back to whatever regional format the webapp uses
// function numToString(num, decimals) {
//     return df.sys.data.numToString(num, decimalSeparator, decimals);
// }

class OpenLayersComponent extends df.WebBaseControl {
    /** @type WebGLTileLayer */
    #baseLayer;
    /** @type VectorLayer */
    #drawLayer;
    /** @type VectorLayer */
    #vectorLayer;
    /** @type Map */
    #map;
    /** @type Draw */
    #drawInteraction;
    /** @type Modify */
    #modifyInteraction;
    /** @type Snap */
    #snapInteraction;
    /** @type {import('ol/Feature').FeatureLike} */
    #currentFeature;
    /** @type {import('ol/events').EventsKey} */
    #hoverHandler;
    /** @type Select */
    #selectInteraction;
    /** @type Translate */
    #translateInteraction;
    /** @type number */
    #panTimer;
    /** @type {import('ol/style/Style').StyleFunction} */
    #defaultStyleFunction;
    /** @type Element */
    #tooltip;

    constructor(sName, oParent) {
        super(sName, oParent);
        this._sControlClass = 'openlayers';
        decimalSeparator = this.getWebApp().psDecimalSeparator;

        // Define web properties and events here
        this.prop(df.tString, 'psGoogleAPIKey');
        this.prop(df.tString, 'psMapType');
        this.prop(df.tString, 'psProjection');
        this.prop(df.tNumber, 'pnLongitude');
        this.prop(df.tNumber, 'pnLatitude');
        this.prop(df.tNumber, 'pnZoomLevel');
        this.prop(df.tBool, 'pbShowLengths');
        this.prop(df.tString, 'psVectorStyle');
        this.prop(df.tInt, 'piHitTolerance');
        this.prop(df.tBool, 'pbShowTooltips');

        this.addSync('pnLongitude');
        this.addSync('pnLatitude');
        this.addSync('pnZoomLevel');

        this.event('OnDraw');
        this.event('OnEdit');
        this.event('OnHover');
        this.event('OnSelect');
        this.event('OnMove');
        this.event('OnViewChange');
        this.event('OnExport');
        this.event('OnRightClick');
    }

    openHtml(aHtml) {
        super.openHtml(aHtml);
        aHtml.push(`<div id="${this._sControlId}"><div class="dfol-tooltip"></div></div>`);
    }

    afterRender() {
        this._eControl = df.dom.query(this._eElem, `#${this._sControlId}`);
        this._eControl.style.position = 'relative';
        this.#tooltip = df.dom.query(this._eControl, '.dfol-tooltip');
        super.afterRender();

        this.set_psVectorStyle(this.psVectorStyle);

        this.#baseLayer = new WebGLTileLayer();
        this.#createSource();

        this.#drawLayer = new VectorLayer({
            source: new VectorSource({ wrapX: false })
        });

        this.#vectorLayer = new VectorLayer({
            source: new VectorSource({ wrapX: false }),
            style: (feature, resolution) => styleFunction(feature, resolution, this.pbShowLengths, this.#defaultStyleFunction)
        });

        this.#map = new Map({
            target: this._sControlId,
            layers: [this.#baseLayer, this.#vectorLayer, this.#drawLayer],
            view: new View({
                projection: this.psProjection,
                center: fromLonLat([this.pnLongitude, this.pnLatitude], this.psProjection),
                zoom: this.pnZoomLevel
            })
        });

        this.#map.on('moveend', () => {
            const view = this.#map.getView();
            const [lon, lat] = toLonLat(view.getCenter(), this.psProjection);
            this.pnLongitude = lon;
            this.pnLatitude = lat;
            this.pnZoomLevel = view.getZoom();
            this.fire('OnViewChange');
        });

        this.set_pbServerOnHover(this.pbServerOnHover);
        this.set_psClientOnHover(this.psClientOnHover);
        this.set_pbShowTooltips(this.pbShowTooltips);

        this.#map.getViewport().addEventListener('contextmenu', event => {
            const enabled = this.pbServerOnRightClick || this.psClientOnRightClick;
            if (!enabled || event.target.closest('.ol-control')) return;
            event.preventDefault();
            const [lon, lat] = toLonLat(this.#map.getEventCoordinate(event), this.psProjection);
            const feature = this.#map.forEachFeatureAtPixel(
                this.#map.getEventPixel(event),
                feature => feature,
                { hitTolerance: this.piHitTolerance }
            );
            const json = feature ? geoJSON.writeFeature(feature) : '';
            this.fire('OnRightClick', [lon, lat, event.clientX, event.clientY, json]);
        });

        this.#drawLayer.getSource().on('addfeature', event => {
            // with options { dataProjection: 'EPSG:4326', featureProjection: this.psProjection } to reproject to lonlat
            const json = geoJSON.writeFeature(event.feature);
            this.fire('OnDraw', [json]);
            event.target.removeFeature(event.feature);
        });
    }

    #createSource() {
        const source = new Google({
            key: this.psGoogleAPIKey,
            mapType: this.psMapType === 'satellite-roadmap' ? 'satellite' : this.psMapType,
            scale: 'scaleFactor2x',
            highDpi: true,
            layerTypes: this.psMapType === 'satellite-roadmap' || this.psMapType === 'terrain' ? ['layerRoadmap'] : []
        });

        source.on('change', () => {
            if (source.getState() === 'error') {
                alert(source.getError());
            }
        });

        this.#baseLayer.setSource(source);
    }

    #addHoverHandler() {
        if (!this.#hoverHandler) {
            this.#hoverHandler = this.#map.on('pointermove', event => {
                let feature;
    
                if (this.#drawInteraction) {
                    return;
                }
    
                const pixel = this.#map.getEventPixel(event.originalEvent);
                if (event.dragging) {
                    feature = undefined;
                }
                else {
                    feature = event.originalEvent.target.closest('.ol-control')
                        ? undefined
                        : this.#map.forEachFeatureAtPixel(pixel, feature => feature);
                }
    
                if (feature !== this.#currentFeature) {
                    this.#currentFeature = feature;
                    const json = feature ? geoJSON.writeFeature(feature) : '';
                    this.fire('OnHover', [json]);
                    if (this.pbShowTooltips && feature) {
                        this.#setTooltip('');
                        this.#tooltip.style.left = `${pixel[0]}px`;
                        this.#tooltip.style.top = `${pixel[1]}px`;
                        this.serverAction('GetTooltip', [json], null, event => this.#setTooltip(event.sReturnValue));
                    }
                    if (!feature) {
                        this.#setTooltip('');
                    }
                }
                else if (feature && this.pbShowTooltips) {
                    this.#tooltip.style.left = `${pixel[0]}px`;
                    this.#tooltip.style.top = `${pixel[1]}px`;
                }
            });
        }
    }

    #setTooltip(value) {
        if (value) {
            this.#tooltip.style.visibility = 'visible';
            this.#tooltip.innerHTML = value;
        }
        else {
            this.#tooltip.style.visibility = 'hidden';
        }
    }

    #removeHoverHandler() {
        if (this.#hoverHandler) {
            unByKey(this.#hoverHandler);
            this.#hoverHandler = undefined;
        }
    }

    #panZoom() {
        const view = this.#map.getView();
        view.setCenter(fromLonLat([this.pnLongitude, this.pnLatitude], this.psProjection));
        view.setZoom(this.pnZoomLevel);
        this.#panTimer = undefined;
    }

    // Setters

    set_psMapType() {
        setTimeout(() => this.#createSource());
    }

    set_pbServerOnHover(value) {
        if (value || this.psClientOnHover || this.pbShowTooltips) {
            this.#addHoverHandler();
        }
        else {
            this.#removeHoverHandler();
        }
    }

    set_psClientOnHover(value) {
        if (value || this.pbServerOnHover || this.pbShowTooltips) {
            this.#addHoverHandler();
        }
        else {
            this.#removeHoverHandler();
        }
    }

    set_pbShowTooltips(value) {
        if (value || this.pbServerOnHover || this.psClientOnHover) {
            this.#addHoverHandler();
        }
        else {
            this.#removeHoverHandler();
        }
    }

    set_pnLongitude() {
        if (!this.#panTimer) {
            this.#panTimer = setTimeout(() => this.#panZoom());
        }
    }

    set_pnLatitude() {
        if (!this.#panTimer) {
            this.#panTimer = setTimeout(() => this.#panZoom());
        }
    }

    set_pnZoomLevel() {
        if (!this.#panTimer) {
            this.#panTimer = setTimeout(() => this.#panZoom());
        }
    }

    set_pbShowLengths() {
        setTimeout(() => this.#vectorLayer.changed());
    }

    set_psVectorStyle(value) {
        if (value) {
            this.#defaultStyleFunction = toStyleFunction(JSON.parse(value));
        }
        else {
            this.#defaultStyleFunction = undefined;
        }
        this.#vectorLayer?.changed();
    }

    // Methods

    activateDraw(type, style = '', vertexCount = 0) {
        let geometryFunction;
        if (type === 'Circle') {
            geometryFunction = createRegularPolygon(parseInt(vertexCount, 10));
        }
        if (type === 'Box') {
            type = 'Circle';
            geometryFunction = createBox();
        }
        if (style === '') {
            style = undefined;
        } else {
            style = JSON.parse(style);
        }
        this.deactivateDraw();
        this.#drawInteraction = new Draw({
            source: this.#drawLayer.getSource(),
            type,
            style,
            geometryFunction
        });
        this.#map.addInteraction(this.#drawInteraction);
    }

    deactivateDraw() {
        this.#map.removeInteraction(this.#drawInteraction);
        this.#drawInteraction = undefined;
    }

    activateEdit() {
        this.deactivateEdit();
        this.#modifyInteraction = new Modify({
            source: this.#vectorLayer.getSource()
        });
        this.#map.addInteraction(this.#modifyInteraction);
        this.#snapInteraction = new Snap({
            source: this.#vectorLayer.getSource()
        });
        this.#map.addInteraction(this.#snapInteraction);

        this.#modifyInteraction.on('modifyend', event => {
            const json = geoJSON.writeFeature(event.features.item(0));
            this.fire('OnEdit', [json]);
        });
    }

    deactivateEdit() {
        this.#map.removeInteraction(this.#modifyInteraction);
        this.#modifyInteraction = undefined;
        this.#map.removeInteraction(this.#snapInteraction);
        this.#snapInteraction = undefined;
    }

    activateSelect() {
        this.deactivateSelect();
        this.#selectInteraction = new Select({ hitTolerance: this.piHitTolerance });
        this.#translateInteraction = new Translate({
            features: this.#selectInteraction.getFeatures()
        });
        this.#map.addInteraction(this.#selectInteraction);
        this.#map.addInteraction(this.#translateInteraction);

        this.#selectInteraction.on('select', event => {
            const json = geoJSON.writeFeatures(event.selected);
            this.fire('OnSelect', [json]);
        });
        this.#translateInteraction.on('translateend', event => {
            const json = geoJSON.writeFeatures(event.features.getArray());
            this.fire('OnMove', [json]);
        });
    }

    deactivateSelect() {
        this.#map.removeInteraction(this.#selectInteraction);
        this.#selectInteraction = undefined;
        this.#map.removeInteraction(this.#translateInteraction);
        this.#translateInteraction = undefined;
    }

    addFeatures(json, flatStyle) {
        const features = geoJSON.readFeatures(json);
        if (flatStyle) {
            const styleFunction = toStyleFunction(JSON.parse(flatStyle));
            for (const feature of features) {
                feature.setStyle(styleFunction);
            }
        }
        this.#vectorLayer.getSource().addFeatures(features);
    }

    removeSelectedFeatures() {
        if (this.#selectInteraction) {
            this.#selectInteraction.getFeatures().forEach(feature => this.#vectorLayer.getSource().removeFeature(feature));
        }
    }

    addMarker(lon, lat, flatStyle) {
        const feature = new Feature({
            geometry: new Point(
                fromLonLat([stringToNum(lon), stringToNum(lat)], this.psProjection)
            )
        });
        feature.setStyle(toStyleFunction(JSON.parse(flatStyle)));
        this.#vectorLayer.getSource().addFeature(feature);
    }

    reset() {
        this.#vectorLayer.getSource().clear();
    }

    export() {
        this.#map.once('rendercomplete', () => {
            const url = exportMap(this.#map);
            this.fire('OnExport', [url]);
        });
        this.#map.renderSync();
    }

    zoomTo(west, south, east, north, duration = 0) {
        const [minX, minY] = fromLonLat([stringToNum(west), stringToNum(south)], this.psProjection);
        const [maxX, maxY] = fromLonLat([stringToNum(east), stringToNum(north)], this.psProjection);
        this.#map.getView().fit([minX, minY, maxX, maxY], { duration });
    }

    zoomToContents(padding) {
        const extent = this.#vectorLayer.getSource().getExtent();
        if (!isEmpty(extent)) {
            this.#map.getView().fit(extent, { padding: [padding, padding, padding, padding] });
        }
    }
}

export default global.OpenLayersComponent = OpenLayersComponent;